import { MealSlotConfig, NutritionPhase, Recipe, ProteinReference, MealSlot, MealEntry, DayMode } from '../types';
import { differenceInWeeks, isWeekend } from 'date-fns';

// --- Nutrition Phases ---

export const NUTRITION_PHASES: NutritionPhase[] = [
  {
    id: 1,
    name: 'Phase 1 - Foundation',
    weekStart: 1,
    weekEnd: 4,
    proteinMin: 120,
    proteinMax: 140,
    weekdayCalories: 2300,
    weekendCalories: 2000,
    focus: 'Establish the meal timing structure. Set the alarms. Build the habit of eating at the right times even when suppressed.',
    keyChange: 'Add the 10:30am snack. Upgrade breakfast. These two alone transform how evenings feel.',
    watchOut: "Don't cut calories aggressively - you're starting exercise and your body needs fuel.",
  },
  {
    id: 2,
    name: 'Phase 2 - Tightening',
    weekStart: 5,
    weekEnd: 10,
    proteinMin: 140,
    proteinMax: 160,
    weekdayCalories: 2200,
    weekendCalories: 1900,
    focus: 'Tighten protein at each meal. Cook from the dinner list consistently. Pre-ride snack becomes important.',
    keyChange: 'Replace both coconut waters with water. Add the 4:30pm pre-ride snack.',
    watchOut: "The 'I'm not hungry at lunch' trap - the meds are working, but you still need protein at lunch.",
  },
  {
    id: 3,
    name: 'Phase 3 - Recovery Focus',
    weekStart: 11,
    weekEnd: 20,
    proteinMin: 150,
    proteinMax: 170,
    weekdayCalories: 2300,
    weekendCalories: 1900,
    focus: 'Post-ride recovery nutrition matters more now. The 30-min window after arriving home for a small protein snack is worth being consistent about.',
    keyChange: 'Consider a protein shake post-ride if hitting 160g from food alone feels hard.',
    watchOut: "Under-eating on big ride days. You're burning significantly more - don't drop calories while increasing effort.",
  },
  {
    id: 4,
    name: 'Phase 4 - Recomposition',
    weekStart: 21,
    weekEnd: null,
    proteinMin: 160,
    proteinMax: 180,
    weekdayCalories: 2500,
    weekendCalories: 2000,
    focus: 'Body recomposition - losing fat while building fitness. Protein is the lever that lets both happen simultaneously.',
    keyChange: 'Larger breakfast to fuel the morning ride in (when you start biking both ways). 3 eggs + yoghurt + granola.',
    watchOut: "Cutting calories too low. At this point you're doing 80-100 miles/week. Eat enough to perform.",
  },
];

// --- Weekday Meal Slots (6 eating windows) ---

export const WEEKDAY_MEAL_SLOTS: MealSlotConfig[] = [
  {
    slot: 'breakfast',
    label: 'Breakfast',
    timeWindow: '7:00-7:30am',
    targetProtein: 32,
    targetCalories: 420,
    weekdayOnly: false,
    notes: 'Eat before meds kick in. 2 eggs + 200g Greek yoghurt + 40-50g Lizi\'s Low Sugar Granola + optional berries.',
  },
  {
    slot: 'mid-morning-snack',
    label: 'Mid-Morning Snack',
    timeWindow: '10:30-11:00am',
    targetProtein: 18,
    targetCalories: 220,
    weekdayOnly: true,
    requiresAlarm: true,
    notes: 'You will NOT feel hungry. Eat anyway. Options: Greek yoghurt + berries, 2 boiled eggs, nuts + banana, cottage cheese + rice cakes.',
  },
  {
    slot: 'lunch',
    label: 'Lunch',
    timeWindow: '1:00-2:00pm',
    targetProtein: 35,
    targetCalories: 550,
    weekdayOnly: false,
    notes: 'Protein anchor visible and prominent. Eat all protein and veg, half the carbs. Water only.',
  },
  {
    slot: 'pre-ride-snack',
    label: 'Pre-Ride Snack',
    timeWindow: '4:30-5:00pm',
    targetProtein: 12,
    targetCalories: 220,
    weekdayOnly: true,
    notes: 'Banana + peanut butter, or small Greek yoghurt, or protein bar (20g+ protein, under 250 cal). Eat 30-45 min before leaving.',
  },
  {
    slot: 'post-ride-snack',
    label: 'Post-Ride Snack',
    timeWindow: '~7:00pm',
    targetProtein: 10,
    targetCalories: 100,
    weekdayOnly: true,
    notes: 'Shower first. Small recovery snack while cooking: 1 boiled egg or small yoghurt. Start cooking within 45 min.',
  },
  {
    slot: 'dinner',
    label: 'Dinner',
    timeWindow: '7:30-8:30pm',
    targetProtein: 50,
    targetCalories: 550,
    weekdayOnly: false,
    notes: 'Your main protein hit. High protein main (45-55g), large portion of veg/salad, moderate carbs.',
  },
];

// --- Weekend Meal Slots (flexible) ---

export const WEEKEND_MEAL_SLOTS: MealSlotConfig[] = [
  {
    slot: 'breakfast',
    label: 'Breakfast (optional)',
    timeWindow: 'When hungry',
    targetProtein: 20,
    targetCalories: 350,
    weekdayOnly: false,
    notes: "Don't force it if not hungry. No medication means no compensation spiral from skipping. Light if eaten: yoghurt + granola, eggs, or fruit.",
  },
  {
    slot: 'lunch',
    label: 'Lunch',
    timeWindow: '~12:00pm',
    targetProtein: 35,
    targetCalories: 550,
    weekdayOnly: false,
    notes: 'Protein anchor, everything else flexible. Choose a meal with clear protein-forward centre.',
  },
  {
    slot: 'snack',
    label: 'Afternoon Snack (if needed)',
    timeWindow: 'When peckish',
    targetProtein: 12,
    targetCalories: 200,
    weekdayOnly: false,
    notes: 'Yoghurt, nuts, or boiled eggs if you get peckish between meals. No alarm needed.',
  },
  {
    slot: 'dinner',
    label: 'Dinner',
    timeWindow: '~7:00pm',
    targetProtein: 45,
    targetCalories: 600,
    weekdayOnly: false,
    notes: 'Eat out freely. Protein-forward main when easy (steak, fish, chicken). Watch liquid calories - beer/wine over cocktails.',
  },
];

// --- 15 Dinner Recipes ---

export const DINNER_RECIPES: Recipe[] = [
  {
    id: 'poke-bowl',
    name: 'Poke Bowl - Tataki Tuna',
    protein: 52,
    calories: 580,
    prepTime: 20,
    ingredients: ['Tuna (tataki)', 'Sushi rice (reduce by 1/3)', 'Edamame', 'Vegetables', 'Sherry vinegar + soy + sesame oil + honey marinade'],
    tip: 'Add extra edamame (17g protein/100g) to push protein higher. Consider swapping some rice for cauliflower rice.',
    tags: ['fish', 'bowl', 'monday'],
    mealCategories: ['dinner'],
  },
  {
    id: 'salmon-rice-spinach',
    name: 'Salmon + Microwave Rice + Wilted Spinach',
    protein: 42,
    calories: 540,
    prepTime: 12,
    ingredients: ['Salmon fillet', 'Microwave rice', 'Bag of spinach', 'Soy sauce', 'Lemon'],
    tip: 'Season salmon with soy + lemon. Spinach straight into the pan for 2 min after the salmon.',
    tags: ['fish', 'quick'],
    mealCategories: ['dinner'],
  },
  {
    id: 'chicken-stirfry',
    name: 'Chicken Thighs + Frozen Veg Stir-Fry + Egg Noodles',
    protein: 48,
    calories: 560,
    prepTime: 15,
    ingredients: ['Chicken thighs', 'Frozen stir-fry veg', 'Egg noodles', 'Soy sauce', 'Garlic', 'Ginger'],
    tip: 'Pre-marinate thighs in the morning (soy + garlic + ginger). Frozen veg straight in the wok.',
    tags: ['chicken', 'stir-fry'],
    mealCategories: ['dinner'],
  },
  {
    id: 'tuna-pasta',
    name: 'Tuna + Wholegrain Pasta + Cherry Tomatoes + Olive Oil',
    protein: 44,
    calories: 530,
    prepTime: 13,
    ingredients: ['Tinned tuna', 'Wholegrain pasta', 'Cherry tomatoes', 'Olive oil', 'Black pepper'],
    tip: 'Pasta cooks in 10 min. Drain, toss with tuna + olive oil + halved tomatoes + black pepper.',
    tags: ['fish', 'pasta', 'simple'],
    mealCategories: ['lunch', 'dinner'],
  },
  {
    id: 'eggs-sourdough',
    name: '4 Eggs + Sourdough + Avocado + Cottage Cheese',
    protein: 42,
    calories: 490,
    prepTime: 10,
    ingredients: ['4 eggs', 'Sourdough bread', 'Avocado', 'Cottage cheese'],
    tip: 'Best on lighter days (Monday/rest). Cottage cheese on the side pushes protein high enough.',
    tags: ['eggs', 'light', 'rest-day'],
    mealCategories: ['dinner'],
  },
  {
    id: 'beef-mince-rice',
    name: 'Beef Mince + Tinned Tomatoes + Cumin + Microwave Rice',
    protein: 46,
    calories: 570,
    prepTime: 15,
    ingredients: ['Beef mince', 'Tinned tomatoes', 'Cumin', 'Garlic powder', 'Microwave rice'],
    tip: 'Brown mince 8 min, add tin of tomatoes + cumin + garlic powder. Serve on microwave rice.',
    tags: ['beef', 'hearty'],
    mealCategories: ['dinner'],
  },
  {
    id: 'mackerel-sweet-potato',
    name: 'Mackerel + Microwave Sweet Potato + Rocket + Lemon',
    protein: 38,
    calories: 500,
    prepTime: 12,
    ingredients: ['Tinned mackerel', 'Sweet potato', 'Rocket', 'Lemon'],
    tip: 'Microwave sweet potato 6 min. Tinned mackerel needs zero cooking. Squeeze lemon over everything.',
    tags: ['fish', 'no-cook-protein'],
    mealCategories: ['lunch', 'dinner'],
  },
  {
    id: 'yoghurt-bowl-eggs',
    name: 'Greek Yoghurt Bowl + Banana + Granola + 2 Boiled Eggs',
    protein: 40,
    calories: 450,
    prepTime: 5,
    ingredients: ['Greek yoghurt', 'Banana', 'Granola', '2 boiled eggs'],
    tip: 'Light night option. The boiled eggs alongside push protein up enough - not optional.',
    tags: ['light', 'no-cook', 'rest-day'],
    mealCategories: ['dinner'],
  },
  {
    id: 'prawn-stirfry',
    name: 'Prawn Stir-Fry + Rice Noodles + Pak Choi',
    protein: 40,
    calories: 480,
    prepTime: 12,
    ingredients: ['Frozen prawns', 'Rice noodles', 'Pak choi', 'Soy sauce', 'Ginger', 'Garlic'],
    tip: 'Frozen prawns defrost in 5 min in the pan. Add soy + ginger + garlic.',
    tags: ['seafood', 'stir-fry'],
    mealCategories: ['dinner'],
  },
  {
    id: 'turkey-pasta',
    name: 'Turkey Mince + Courgette + Tinned Tomatoes + Pasta',
    protein: 50,
    calories: 540,
    prepTime: 15,
    ingredients: ['Turkey mince', 'Courgette', 'Tinned tomatoes', 'Pasta'],
    tip: 'Turkey mince is leaner than beef and higher protein. Grate courgette in - it vanishes into the sauce.',
    tags: ['turkey', 'pasta', 'high-protein'],
    mealCategories: ['dinner'],
  },
  {
    id: 'cod-potatoes-peas',
    name: 'Cod / Haddock Fillet + Crushed New Potatoes + Peas',
    protein: 44,
    calories: 480,
    prepTime: 15,
    ingredients: ['Cod or haddock fillet', 'New potatoes', 'Frozen peas', 'Olive oil'],
    tip: 'Microwave potatoes 6 min, crush with fork + olive oil. Pan-fry fish 4 min each side.',
    tags: ['fish', 'british'],
    mealCategories: ['dinner'],
  },
  {
    id: 'chicken-halloumi',
    name: 'Chicken Breast + Halloumi + Roasted Peppers + Pitta',
    protein: 52,
    calories: 580,
    prepTime: 15,
    ingredients: ['Chicken breast', 'Halloumi', 'Jarred roasted peppers', 'Pitta bread'],
    tip: 'Slice chicken thin - cooks in 8 min. Halloumi 2 min each side. Jarred peppers need no prep.',
    tags: ['chicken', 'high-protein'],
    mealCategories: ['dinner'],
  },
  {
    id: 'sardines-sourdough',
    name: 'Sardines on Sourdough + Boiled Eggs + Watercress',
    protein: 44,
    calories: 460,
    prepTime: 8,
    ingredients: ['Tinned sardines in olive oil', 'Sourdough bread', '2 boiled eggs', 'Watercress'],
    tip: 'Mash sardines onto sourdough. Two boiled eggs alongside. Simple, cheap, and surprisingly good.',
    tags: ['fish', 'no-cook-protein', 'budget'],
    mealCategories: ['lunch', 'dinner'],
  },
  {
    id: 'pork-tenderloin',
    name: 'Pork Tenderloin + Microwave Rice + Tenderstem Broccoli',
    protein: 50,
    calories: 520,
    prepTime: 15,
    ingredients: ['Pork tenderloin', 'Microwave rice', 'Tenderstem broccoli', 'Soy sauce', 'Sesame oil'],
    tip: 'Slice tenderloin thin - cooks in 6-7 min. Steam broccoli 4 min. Soy + sesame oil drizzle.',
    tags: ['pork', 'high-protein'],
    mealCategories: ['dinner'],
  },
  {
    id: 'prawn-avocado-bowl',
    name: 'Prawn + Avocado + Microwave Rice + Lime (No-Cook Bowl)',
    protein: 38,
    calories: 470,
    prepTime: 5,
    ingredients: ['Cooked cold prawns', 'Microwave rice', 'Avocado', 'Lime', 'Soy sauce'],
    tip: 'Use cooked cold prawns and slightly cooled microwave rice. Slice avocado, squeeze lime, drizzle soy. Zero cooking beyond rice.',
    tags: ['seafood', 'no-cook', 'quick'],
    mealCategories: ['lunch', 'dinner'],
  },
];

// --- Breakfast Recipes ---

export const BREAKFAST_RECIPES: Recipe[] = [
  {
    id: 'eggs-yoghurt-granola',
    name: '2 Eggs + Greek Yoghurt + Granola',
    protein: 32,
    calories: 420,
    prepTime: 8,
    ingredients: ['2 eggs', '200g Greek yoghurt (full fat)', '40-50g Lizi\'s Low Sugar Granola', 'Optional berries'],
    tip: 'Eat before meds kick in. Scramble or fry eggs - whatever gets them eaten. Yoghurt + granola on the side.',
    tags: ['high-protein', 'default'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'overnight-oats',
    name: 'Overnight Oats + Protein Powder + Berries',
    protein: 30,
    calories: 380,
    prepTime: 5,
    ingredients: ['50g oats', '1 scoop protein powder', '150ml milk', '100g mixed berries', '1 tbsp chia seeds'],
    tip: 'Prep the night before - zero morning effort. Add berries fresh in the morning for texture.',
    tags: ['no-cook', 'meal-prep', 'quick'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'smoked-salmon-sourdough',
    name: 'Smoked Salmon + Sourdough + Cream Cheese',
    protein: 28,
    calories: 400,
    prepTime: 5,
    ingredients: ['100g smoked salmon', '2 slices sourdough', 'Cream cheese', 'Capers', 'Lemon'],
    tip: 'Toast sourdough, spread cream cheese, lay salmon. Capers + lemon squeeze. Weekend-worthy but weekday-fast.',
    tags: ['fish', 'no-cook', 'quick'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'protein-pancakes',
    name: 'Protein Pancakes (Banana + Eggs + Oats)',
    protein: 26,
    calories: 380,
    prepTime: 12,
    ingredients: ['1 banana', '2 eggs', '40g oats', '1 scoop protein powder (optional)', 'Berries to serve'],
    tip: 'Blend everything, cook like normal pancakes. Makes 3-4 small ones. Top with berries and a drizzle of honey.',
    tags: ['sweet', 'weekend'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'cottage-cheese-bowl',
    name: 'Cottage Cheese Bowl + Fruit + Nuts',
    protein: 28,
    calories: 350,
    prepTime: 3,
    ingredients: ['200g cottage cheese', 'Banana or berries', '20g mixed nuts', 'Drizzle of honey'],
    tip: 'Zero cooking. High protein, low effort. Add a handful of granola if you need more carbs.',
    tags: ['no-cook', 'quick', 'high-protein'],
    mealCategories: ['breakfast', 'snack'],
  },
  {
    id: 'turkish-eggs',
    name: 'Turkish Eggs (Cilbir) + Sourdough',
    protein: 30,
    calories: 440,
    prepTime: 10,
    ingredients: ['2 eggs', '200g Greek yoghurt', '1 tbsp butter', '1 tsp chilli flakes', 'Sourdough bread', 'Fresh dill'],
    tip: 'Warm yoghurt gently. Poach eggs. Melt butter with chilli flakes and drizzle over. Restaurant-quality in 10 min.',
    tags: ['eggs', 'weekend'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'scrambled-eggs-avocado',
    name: 'Scrambled Eggs + Avocado Toast',
    protein: 24,
    calories: 420,
    prepTime: 8,
    ingredients: ['3 eggs', '2 slices sourdough', '1/2 avocado', 'Cherry tomatoes', 'Black pepper'],
    tip: 'Low heat, constant stirring for creamy scrambled eggs. Take off heat slightly before done - they keep cooking.',
    tags: ['eggs', 'quick'],
    mealCategories: ['breakfast'],
  },
  {
    id: 'protein-smoothie',
    name: 'Protein Smoothie (Banana + Yoghurt + PB)',
    protein: 30,
    calories: 400,
    prepTime: 3,
    ingredients: ['1 banana', '150g Greek yoghurt', '1 tbsp peanut butter', '200ml milk', '1 scoop protein powder (optional)'],
    tip: 'Blend everything. Drink on the way out if rushed. Protein powder optional if using Greek yoghurt.',
    tags: ['quick', 'no-cook', 'on-the-go'],
    mealCategories: ['breakfast'],
  },
];

// --- Lunch Recipes ---

export const LUNCH_RECIPES: Recipe[] = [
  {
    id: 'chicken-rice-salad',
    name: 'Chicken + Rice + Mixed Salad',
    protein: 38,
    calories: 520,
    prepTime: 15,
    ingredients: ['Chicken breast or thigh (150g)', 'Microwave rice', 'Mixed salad leaves', 'Cherry tomatoes', 'Olive oil dressing'],
    tip: 'Pan-fry chicken 8 min. Microwave rice 2 min. Protein anchor visible and prominent.',
    tags: ['chicken', 'high-protein'],
    mealCategories: ['lunch'],
  },
  {
    id: 'tuna-sourdough-sandwich',
    name: 'Tuna Mayo + Sourdough Sandwich',
    protein: 34,
    calories: 480,
    prepTime: 5,
    ingredients: ['1 tin tuna (drained)', '1 tbsp mayo', 'Sourdough bread', 'Lettuce', 'Cucumber', 'Black pepper'],
    tip: 'Classic and fast. Mix tuna + mayo + pepper. Load the sandwich. Done in 5 min.',
    tags: ['fish', 'quick', 'no-cook', 'budget'],
    mealCategories: ['lunch'],
  },
  {
    id: 'burrito-bowl',
    name: 'Beef Mince Burrito Bowl',
    protein: 40,
    calories: 560,
    prepTime: 15,
    ingredients: ['Beef mince (150g)', 'Microwave rice', 'Tinned black beans', 'Salsa', 'Avocado', 'Cheese'],
    tip: 'Brown mince with cumin + smoked paprika. Layer rice, mince, beans, salsa, avocado. Skip the tortilla.',
    tags: ['beef', 'bowl', 'hearty'],
    mealCategories: ['lunch', 'dinner'],
  },
  {
    id: 'greek-salad-chicken',
    name: 'Greek Salad + Grilled Chicken',
    protein: 36,
    calories: 450,
    prepTime: 12,
    ingredients: ['Chicken breast (150g)', 'Cucumber', 'Tomatoes', 'Red onion', 'Feta', 'Olives', 'Olive oil + lemon dressing'],
    tip: 'Grill chicken, slice over salad. Feta adds protein + flavour. Good summer option.',
    tags: ['chicken', 'salad', 'light'],
    mealCategories: ['lunch'],
  },
  {
    id: 'prawn-noodle-soup',
    name: 'Prawn + Noodle Soup',
    protein: 32,
    calories: 380,
    prepTime: 10,
    ingredients: ['Frozen prawns', 'Rice noodles', 'Chicken stock', 'Pak choi', 'Soy sauce', 'Chilli flakes'],
    tip: 'Boil stock, add noodles + frozen prawns + pak choi. Ready in 10 min. Light but protein-forward.',
    tags: ['seafood', 'soup', 'light', 'quick'],
    mealCategories: ['lunch'],
  },
  {
    id: 'turkey-pitta-wrap',
    name: 'Turkey + Cheese Pitta Wrap',
    protein: 35,
    calories: 460,
    prepTime: 5,
    ingredients: ['Turkey slices (100g)', 'Cheese slice', 'Pitta bread', 'Lettuce', 'Mustard or mayo'],
    tip: 'Quick deli-style lunch. Warm pitta, stuff with turkey + cheese + salad. Zero cooking.',
    tags: ['turkey', 'quick', 'no-cook'],
    mealCategories: ['lunch'],
  },
  {
    id: 'salmon-poke-light',
    name: 'Salmon Poke Bowl (Light)',
    protein: 35,
    calories: 480,
    prepTime: 10,
    ingredients: ['Salmon fillet (120g)', 'Sushi rice (small portion)', 'Edamame', 'Cucumber', 'Soy sauce', 'Sesame seeds'],
    tip: 'Lighter lunch version of the poke bowl. Less rice, more veg. Salmon can be raw or seared.',
    tags: ['fish', 'bowl', 'light'],
    mealCategories: ['lunch'],
  },
  {
    id: 'cottage-cheese-rice-cakes',
    name: 'Cottage Cheese + Rice Cakes + Avocado + Tomatoes',
    protein: 28,
    calories: 380,
    prepTime: 3,
    ingredients: ['200g cottage cheese', '3-4 rice cakes', '1/2 avocado', 'Cherry tomatoes', 'Everything bagel seasoning'],
    tip: 'Spread cottage cheese on rice cakes, top with avocado + tomatoes. Everything seasoning makes it.',
    tags: ['no-cook', 'quick', 'light', 'budget'],
    mealCategories: ['lunch', 'snack'],
  },
];

// --- Snack Recipes ---

export const SNACK_RECIPES: Recipe[] = [
  {
    id: 'yoghurt-berries',
    name: 'Greek Yoghurt + Berries',
    protein: 20,
    calories: 200,
    prepTime: 2,
    ingredients: ['200g Greek yoghurt (full fat)', '80g mixed berries'],
    tip: 'The go-to snack. Always have yoghurt in the fridge. Frozen berries work great too.',
    tags: ['no-cook', 'quick', 'default'],
    mealCategories: ['snack'],
  },
  {
    id: 'boiled-eggs-snack',
    name: '2 Boiled Eggs',
    protein: 12,
    calories: 140,
    prepTime: 1,
    ingredients: ['2 boiled eggs (batch cooked)', 'Pinch of salt', 'Black pepper'],
    tip: 'Batch cook 6-8 eggs on Sunday. Peel and store in fridge. Grab and go all week.',
    tags: ['no-cook', 'quick', 'meal-prep', 'budget'],
    mealCategories: ['snack'],
  },
  {
    id: 'banana-peanut-butter',
    name: 'Banana + Peanut Butter',
    protein: 10,
    calories: 250,
    prepTime: 2,
    ingredients: ['1 banana', '2 tbsp peanut butter'],
    tip: 'Perfect pre-ride fuel. Eat 30-45 min before leaving. Good energy + some protein.',
    tags: ['no-cook', 'quick', 'pre-workout'],
    mealCategories: ['snack'],
  },
  {
    id: 'cottage-cheese-snack',
    name: 'Cottage Cheese + Rice Cakes',
    protein: 26,
    calories: 220,
    prepTime: 2,
    ingredients: ['200g cottage cheese', '2-3 rice cakes'],
    tip: 'High protein, very filling. Add a sprinkle of chilli flakes or everything seasoning.',
    tags: ['no-cook', 'quick', 'high-protein'],
    mealCategories: ['snack'],
  },
  {
    id: 'edamame-snack',
    name: 'Edamame (Microwaved)',
    protein: 17,
    calories: 190,
    prepTime: 3,
    ingredients: ['100g frozen edamame', 'Soy sauce', 'Sea salt'],
    tip: 'Microwave 3 min from frozen. Sprinkle with sea salt. 17g protein per 100g - seriously underrated snack.',
    tags: ['quick', 'high-protein'],
    mealCategories: ['snack'],
  },
  {
    id: 'protein-bar-guide',
    name: 'Protein Bar',
    protein: 20,
    calories: 230,
    prepTime: 0,
    ingredients: ['1 protein bar (20g+ protein, under 250 cal)'],
    tip: 'Look for: 20g+ protein, under 250 cal, under 5g sugar. Good brands: Grenade, Barebells, PhD Smart. Keep one in your bag.',
    tags: ['no-cook', 'on-the-go', 'quick'],
    mealCategories: ['snack'],
  },
];

// --- All Recipes Combined ---

export const ALL_RECIPES: Recipe[] = [
  ...BREAKFAST_RECIPES,
  ...LUNCH_RECIPES,
  ...DINNER_RECIPES,
  ...SNACK_RECIPES,
];

// --- Protein Reference Table ---

export const PROTEIN_REFERENCE: ProteinReference[] = [
  { food: 'Greek yoghurt (200g, full fat)', amount: '200g', protein: 20, bestTiming: 'Breakfast / Snack', prepEffort: 'Zero prep' },
  { food: '2 eggs', amount: '2 eggs', protein: 12, bestTiming: 'Breakfast', prepEffort: '5 min' },
  { food: '4 eggs', amount: '4 eggs', protein: 24, bestTiming: 'Dinner', prepEffort: '5 min' },
  { food: 'Chicken thigh (150g cooked)', amount: '150g', protein: 38, bestTiming: 'Dinner', prepEffort: '10 min pan-fry' },
  { food: 'Salmon fillet (150g)', amount: '150g', protein: 35, bestTiming: 'Dinner', prepEffort: '10 min' },
  { food: 'Tinned tuna (1 tin, 145g drained)', amount: '145g', protein: 32, bestTiming: 'Lunch / Dinner', prepEffort: 'Zero prep' },
  { food: 'Tinned mackerel (1 tin)', amount: '1 tin', protein: 26, bestTiming: 'Lunch / Dinner', prepEffort: 'Zero prep' },
  { food: 'Beef mince (150g cooked)', amount: '150g', protein: 36, bestTiming: 'Dinner', prepEffort: '10 min' },
  { food: 'Turkey mince (150g cooked)', amount: '150g', protein: 42, bestTiming: 'Dinner', prepEffort: '10 min' },
  { food: 'Cottage cheese (200g)', amount: '200g', protein: 24, bestTiming: 'Snack / Lunch', prepEffort: 'Zero prep' },
  { food: 'Edamame (100g)', amount: '100g', protein: 17, bestTiming: 'Snack / Poke bowl', prepEffort: 'Microwave 3 min' },
  { food: 'Boiled eggs x2 (batch cooked)', amount: '2 eggs', protein: 12, bestTiming: 'Snack', prepEffort: 'Batch on Sunday' },
  { food: 'Peanut butter (2 tbsp)', amount: '2 tbsp', protein: 8, bestTiming: 'Snack with banana', prepEffort: 'Zero prep' },
];

// --- Non-Negotiables ---

export const NON_NEGOTIABLES = [
  'Eat breakfast before the meds kick in - you have ~90 min after waking where appetite is natural.',
  'Set a 10:30am alarm and eat the snack - you will not feel hungry, eat it anyway.',
  'On weekends - eat when hungry, keep protein high. Your hunger signals are reliable.',
];

// --- Helper Functions ---

export function getDayMode(date: Date): DayMode {
  return isWeekend(date) ? 'weekend' : 'weekday';
}

export function getMealSlotsForDate(date: Date): MealSlotConfig[] {
  return getDayMode(date) === 'weekend' ? WEEKEND_MEAL_SLOTS : WEEKDAY_MEAL_SLOTS;
}

export function getCurrentPhase(startDate: string | undefined): NutritionPhase {
  if (!startDate) return NUTRITION_PHASES[0];
  const weeks = differenceInWeeks(new Date(), new Date(startDate)) + 1;
  const phase = NUTRITION_PHASES.find(
    p => weeks >= p.weekStart && (p.weekEnd === null || weeks <= p.weekEnd)
  );
  return phase || NUTRITION_PHASES[NUTRITION_PHASES.length - 1];
}

export function getDailyProteinTarget(phase: NutritionPhase): { min: number; max: number } {
  return { min: phase.proteinMin, max: phase.proteinMax };
}

export function getDailyCalorieTarget(phase: NutritionPhase, dayMode: DayMode): number {
  return dayMode === 'weekend' ? phase.weekendCalories : phase.weekdayCalories;
}

export function calculateDailyProtein(meals: MealEntry[], date: string): number {
  return meals
    .filter(m => m.date === date && m.completed)
    .reduce((sum, m) => sum + (m.protein || 0), 0);
}

export function calculateDailyCalories(meals: MealEntry[], date: string): number {
  return meals
    .filter(m => m.date === date && m.completed)
    .reduce((sum, m) => sum + (m.calories || 0), 0);
}

export function getMealSlotLabel(slot: MealSlot): string {
  const labels: Record<MealSlot, string> = {
    'breakfast': 'Breakfast',
    'mid-morning-snack': 'Mid-Morning Snack',
    'lunch': 'Lunch',
    'pre-ride-snack': 'Pre-Ride Snack',
    'post-ride-snack': 'Post-Ride Snack',
    'dinner': 'Dinner',
    'snack': 'Snack',
  };
  return labels[slot] || slot;
}

export function getMealSlotColor(slot: MealSlot): string {
  const colors: Record<MealSlot, string> = {
    'breakfast': '#2A5C45',
    'mid-morning-snack': '#1D6986',
    'lunch': '#6B4F9E',
    'pre-ride-snack': '#8A5A1A',
    'post-ride-snack': '#B55A1A',
    'dinner': '#8A2A1A',
    'snack': '#9c27b0',
  };
  return colors[slot] || '#666';
}

export function getCompletedSlotsCount(meals: MealEntry[], date: string): number {
  return meals.filter(m => m.date === date && m.completed).length;
}

export function getTotalSlotsForDate(date: Date): number {
  return getMealSlotsForDate(date).length;
}
